import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, CheckCircle2, FileEdit, Clock, Award, Zap, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rich Text Editor Component
const SelfWrittenEditor = ({ articleContent, setArticleContent, saving, handleSave, setSubmissionType }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      TextStyle,
      Color,
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder: 'Start writing your amazing startup story here...',
      }),
    ],
    content: articleContent || '',
    onUpdate: ({ editor }) => {
      setArticleContent(editor.getHTML());
    },
  });

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          editor.chain().focus().setImage({ src: reader.result }).run();
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const MenuBar = () => {
    if (!editor) return null;

    return (
      <div className="border-b border-gray-300 p-2 flex flex-wrap gap-1 bg-gray-50">
        {/* Headings */}
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className="h-8 px-2"
        >
          H1
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className="h-8 px-2"
        >
          H2
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className="h-8 px-2"
        >
          H3
        </Button>
        <div className="w-px h-8 bg-gray-300 mx-1" />
        
        {/* Text Formatting */}
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('bold') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleBold().run()}
          className="h-8 px-2 font-bold"
        >
          B
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('italic') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className="h-8 px-2 italic"
        >
          I
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('underline') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className="h-8 px-2 underline"
        >
          U
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('strike') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className="h-8 px-2 line-through"
        >
          S
        </Button>
        <div className="w-px h-8 bg-gray-300 mx-1" />
        
        {/* Text Alignment */}
        <Button
          type="button"
          size="sm"
          variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className="h-8 px-2"
        >
          ⬅
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className="h-8 px-2"
        >
          ↔
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className="h-8 px-2"
        >
          ➡
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive({ textAlign: 'justify' }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className="h-8 px-2"
        >
          ⬌
        </Button>
        <div className="w-px h-8 bg-gray-300 mx-1" />
        
        {/* Lists */}
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('bulletList') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className="h-8 px-2"
        >
          • List
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('orderedList') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className="h-8 px-2"
        >
          1. List
        </Button>
        <div className="w-px h-8 bg-gray-300 mx-1" />
        
        {/* Other */}
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('blockquote') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className="h-8 px-2"
        >
          &quot;
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="h-8 px-2"
        >
          ―
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addImage}
          className="h-8 px-2"
        >
          🖼 Image
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="p-6 border-2 border-blue-500 rounded-lg bg-blue-50/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileEdit className="h-5 w-5 text-blue-600" />
            <Label className="text-lg font-bold text-blue-900">Write Your Own Story</Label>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setSubmissionType('Questionnaire')}
            disabled={saving}
          >
            ← Back to Questions
          </Button>
        </div>
        <p className="text-sm text-blue-700 mb-4">
          Write your complete startup story. Include your introduction, journey, challenges, achievements, team, and future plans. This will be published as-is after editorial review.
        </p>
        
        {/* TipTap Rich Text Editor */}
        <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
          <MenuBar />
          <EditorContent 
            editor={editor} 
            className="prose prose-sm max-w-none p-4 min-h-[400px] focus:outline-none"
          />
        </div>
        
        <p className="text-xs text-muted-foreground mt-2">
          💡 Use the toolbar above to format your story with headings, bold text, lists, and more.
        </p>
      </div>
      
      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => handleSave(true, 'Self Written')}
          disabled={saving || !articleContent || articleContent.trim() === '<p></p>' || articleContent.trim() === ''}
          className="flex-1"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Draft'}
        </Button>
        <Button
          type="button"
          variant="default"
          onClick={() => handleSave(false, 'Self Written')}
          disabled={saving || !articleContent || articleContent.trim() === '<p></p>' || articleContent.trim() === ''}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit Story'}
        </Button>
      </div>
    </div>
  );
};

export default function SubmitStory() {
  const { storyToken } = useParams();
  const navigate = useNavigate();
  
  // States
  const [loading, setLoading] = useState(true);
  const [startup, setStartup] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [expertRequest, setExpertRequest] = useState(null);
  
  // OTP states
  const [step, setStep] = useState('email'); // 'email', 'otp', 'form'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  
  // Form states
  const [saving, setSaving] = useState(false);
  const [submissionType, setSubmissionType] = useState('Questionnaire'); // Questionnaire, Self Written, Expert Written
  const [bannerPhoto, setBannerPhoto] = useState(null);
  const [founderProfiles, setFounderProfiles] = useState([]);
  const [articleContent, setArticleContent] = useState('');
  const [formData, setFormData] = useState({
    brief_info: '',
    startup_journey: '',
    founder_background: '',
    location_reason: '',
    founder_paranoia: '',
    competition_edge: '',
    work_life_balance: '',
    funding_details: '',
    company_excitement: '',
    daily_schedule: '',
    team_info: ''
  });
  
  // Expert Written Payment Modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchStartupInfo();
  }, [storyToken]);

  const fetchStartupInfo = async () => {
    try {
      const response = await axios.get(`${API}/stories/token/${storyToken}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      setStartup(response.data.data);
      setExpertRequest(response.data.data.expert_request || null);
      setIsExpired(false); // Explicitly set to false when successful
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 410) {
        setIsExpired(true);
      } else {
        toast.error('Invalid story link');
      }
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSendingOtp(true);
    try {
      await axios.post(`${API}/stories/token/${storyToken}/send-otp`, { email });
      toast.success('OTP sent to your email');
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) return;

    setVerifyingOtp(true);
    try {
      const response = await axios.post(`${API}/stories/token/${storyToken}/verify-otp`, {
        email,
        otp
      });
      
      toast.success('Email verified successfully');
      
      // If draft exists, load it
      if (response.data.has_draft && response.data.draft_id) {
        try {
          const draftResponse = await axios.get(`${API}/stories/${response.data.draft_id}`);
          const draft = draftResponse.data.data;
          
          console.log('Loading draft:', draft);
          
          // Load submission type
          if (draft.submission_type) {
            setSubmissionType(draft.submission_type);
          }
          
          // Load banner photo
          if (draft.banner_photo) {
            setBannerPhoto(draft.banner_photo);
          }
          
          // Load founder profiles
          if (draft.founder_profiles && draft.founder_profiles.length > 0) {
            setFounderProfiles(draft.founder_profiles);
          }
          
          // Load form data for questionnaire
          if (draft.submission_type === 'Questionnaire' || !draft.submission_type) {
            setFormData({
              brief_info: draft.brief_info || '',
              startup_journey: draft.startup_journey || '',
              founder_background: draft.founder_background || '',
              location_reason: draft.location_reason || '',
              founder_paranoia: draft.founder_paranoia || '',
              competition_edge: draft.competition_edge || '',
              work_life_balance: draft.work_life_balance || '',
              funding_details: draft.funding_details || '',
              company_excitement: draft.company_excitement || '',
              daily_schedule: draft.daily_schedule || '',
              team_info: draft.team_info || ''
            });
          }
          
          // Load article content for self-written
          if (draft.submission_type === 'Self Written' && draft.article_content) {
            setArticleContent(draft.article_content);
          }
          
          toast.success('Draft loaded successfully!');
        } catch (err) {
          console.error('Error loading draft:', err);
          toast.error('Failed to load draft, but you can continue');
        }
      }
      
      setStep('form');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBannerUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFounderProfileUpload = (founderEmail, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFounderProfiles(prev => {
          const existing = prev.find(p => p.founder_email === founderEmail);
          if (existing) {
            return prev.map(p => 
              p.founder_email === founderEmail 
                ? { ...p, profile_picture: reader.result }
                : p
            );
          } else {
            return [...prev, { founder_email: founderEmail, profile_picture: reader.result }];
          }
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (isDraft, type = 'Questionnaire') => {
    // Validate if not a draft
    if (!isDraft && type === 'Questionnaire') {
      const requiredFields = [
        { field: 'brief_info', label: 'Brief info about startup' },
        { field: 'startup_journey', label: 'Startup journey' },
        { field: 'founder_background', label: 'Founder background' },
        { field: 'location_reason', label: 'Location reason' },
        { field: 'founder_paranoia', label: 'Founder paranoia' },
        { field: 'competition_edge', label: 'Competition edge' },
        { field: 'work_life_balance', label: 'Work-life balance' },
        { field: 'funding_details', label: 'Funding details' },
        { field: 'company_excitement', label: 'Company excitement' },
        { field: 'daily_schedule', label: 'Daily schedule' },
        { field: 'team_info', label: 'Team info' },
      ];

      const missingFields = requiredFields.filter(({ field }) => !formData[field] || !formData[field].trim());
      
      if (missingFields.length > 0) {
        toast.error(`Please fill all required fields: ${missingFields.map(f => f.label).join(', ')}`);
        return;
      }
    }

    setSaving(true);
    try {
      let submitData = {
        founder_email: email,
        banner_photo: bannerPhoto || undefined,
        founder_profiles: founderProfiles.length > 0 ? founderProfiles : undefined,
        submission_type: type,
        is_draft: isDraft
      };

      // Add content based on submission type
      if (type === 'Self Written') {
        submitData.article_content = articleContent;
      } else {
        submitData = { ...submitData, ...formData };
      }

      console.log('Saving story with data:', { isDraft, type, token: storyToken });

      const response = await axios.post(
        `${API}/stories/?story_token=${storyToken}`,
        submitData
      );
      
      console.log('Story saved successfully:', response.data);

      if (isDraft) {
        toast.success('Draft saved successfully!');
      } else {
        toast.success('Story submitted successfully!');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err) {
      console.error('Error saving story:', err.response?.data);
      
      // Handle validation errors
      if (err.response?.data?.detail && Array.isArray(err.response.data.detail)) {
        const errorMessages = err.response.data.detail.map(error => {
          if (typeof error === 'object' && error.msg) {
            const field = error.loc ? error.loc.join('.') : 'Field';
            return `${field}: ${error.msg}`;
          }
          return String(error);
        }).join('\n');
        toast.error(errorMessages || 'Validation failed');
      } else if (err.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else {
        toast.error('Failed to save story. Please check your internet connection and try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleExpertWrittenClick = () => {
    setPaymentModalOpen(true);
  };

  const handlePayNow = async () => {
    setProcessingPayment(true);
    
    try {
      // Create expert request
      const requestResponse = await axios.post(`${API}/expert-requests/`, {
        startup_id: startup.startup_id,
        startup_name: startup.startup_name,
        story_token: storyToken,
        founder_email: email,
        founder_name: startup.founders?.find(f => f.email === email)?.name || 'Founder',
        amount: 1999
      });

      const expertRequest = requestResponse.data.data;
      const razorpayKey = requestResponse.data.razorpay_key;

      // Initialize Razorpay
      const options = {
        key: razorpayKey,
        amount: 199900, // Amount in paise (₹1999)
        currency: 'INR',
        name: 'Starticals',
        description: 'Expert Written Story Service',
        handler: async function (response) {
          try {
            // Update expert request with payment details
            await axios.post(`${API}/expert-requests/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id || expertRequest.id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature || 'test_signature',
              request_id: expertRequest.id
            });

            toast.success('Payment successful! Our team will contact you shortly.');
            setPaymentModalOpen(false);
            
            // Refresh page to show expert request status
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          } catch (err) {
            console.error('Payment verification error:', err);
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: startup.founders?.find(f => f.email === email)?.name || 'Founder',
          email: email,
        },
        theme: {
          color: '#FF6F3C'
        },
        modal: {
          ondismiss: function() {
            setProcessingPayment(false);
          }
        },
        notes: {
          request_id: expertRequest.id,
          startup_name: startup.startup_name
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setProcessingPayment(false);
    } catch (err) {
      console.error('Payment error:', err);
      toast.error('Failed to initiate payment. Please try again.');
      setProcessingPayment(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show Expert Request Status if exists
  if (expertRequest) {
    const getStatusColor = (status) => {
      switch (status) {
        case 'pending':
          return 'bg-yellow-500/10 text-yellow-700 border-yellow-500';
        case 'in_progress':
          return 'bg-blue-500/10 text-blue-700 border-blue-500';
        case 'completed':
          return 'bg-green-500/10 text-green-700 border-green-500';
        default:
          return 'bg-gray-500/10 text-gray-700 border-gray-500';
      }
    };

    const getStatusIcon = (status) => {
      switch (status) {
        case 'pending':
          return <Clock className="h-8 w-8 text-yellow-600" />;
        case 'in_progress':
          return <FileEdit className="h-8 w-8 text-blue-600" />;
        case 'completed':
          return <CheckCircle2 className="h-8 w-8 text-green-600" />;
        default:
          return <FileEdit className="h-8 w-8 text-gray-600" />;
      }
    };

    const getStatusMessage = (status) => {
      switch (status) {
        case 'pending':
          return 'Our team will start working on your story soon. We will contact you via email if we need any additional information.';
        case 'in_progress':
          return 'Our expert writers are currently crafting your startup story. You will be notified once it\'s ready for review.';
        case 'completed':
          return 'Your expert written story has been completed and published! Thank you for choosing our premium service.';
        default:
          return 'We have received your request and will get back to you soon.';
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center mb-4">
              {getStatusIcon(expertRequest.status)}
            </div>
            <CardTitle className="text-2xl bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Expert Story Request Received
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Thank you for choosing our Expert Written Story service!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status Badges */}
            <div className="flex flex-col md:flex-row justify-center gap-3">
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Story Writing Status</p>
                <Badge className={`${getStatusColor(expertRequest.status)} px-4 py-2 text-sm font-semibold border-2`}>
                  {expertRequest.status.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-1">Payment Status</p>
                <Badge className={`${expertRequest.payment_status === 'completed' ? 'bg-green-500/10 text-green-700 border-green-500' : 'bg-orange-500/10 text-orange-700 border-orange-500'} px-4 py-2 text-sm font-semibold border-2`}>
                  {expertRequest.payment_status?.toUpperCase() || 'PENDING'}
                </Badge>
              </div>
            </div>

            {/* Status Message */}
            <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <p className="text-center text-blue-900">
                {getStatusMessage(expertRequest.status)}
              </p>
            </div>

            {/* Request Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-accent/50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Startup Name</p>
                <p className="font-semibold">{startup?.startup_name}</p>
              </div>
              <div className="p-4 bg-accent/50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Amount Paid</p>
                <p className="font-semibold">₹{expertRequest.amount?.toLocaleString()}</p>
              </div>
              {expertRequest.created_at && (
                <div className="p-4 bg-accent/50 rounded-lg md:col-span-2">
                  <p className="text-sm text-muted-foreground mb-1">Request Date</p>
                  <p className="font-semibold">
                    {new Date(expertRequest.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
            </div>

            {/* What's Next Section */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                What's Next?
              </h4>
              <ul className="space-y-2 text-sm text-green-800">
                <li className="flex items-start gap-2">
                  <span className="mt-1">•</span>
                  <span>Our expert writers will research and craft your startup story</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1">•</span>
                  <span>We'll contact you via email if we need any additional details</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1">•</span>
                  <span>Expect delivery within 7 business days</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1">•</span>
                  <span>You'll receive a notification once your story is published</span>
                </li>
              </ul>
            </div>

            {/* Action Button */}
            <div className="flex gap-3 pt-4 border-t">
              <Button onClick={() => navigate('/')} className="flex-1" variant="default">
                Go to Homepage
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Link Expired</CardTitle>
            <CardDescription>
              This story submission link has expired. The story has already been submitted.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold font-heading mb-2 text-gradient-primary">
            Submit Your Startup Story
          </h1>
          <p className="text-muted-foreground">
            {startup?.startup_name}
          </p>
        </div>

        {/* Email Step */}
        {step === 'email' && (
          <Card>
            <CardHeader>
              <CardTitle>Verify Your Email</CardTitle>
              <CardDescription>
                Enter your founder email address to receive an OTP
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={sendingOtp}
                  />
                  <p className="text-sm text-muted-foreground">
                    Must be a founder email registered with the startup
                  </p>
                </div>
                <Button type="submit" disabled={sendingOtp || !email} className="w-full">
                  {sendingOtp ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending OTP...</>
                  ) : (
                    'Send OTP'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* OTP Step */}
        {step === 'otp' && (
          <Card>
            <CardHeader>
              <CardTitle>Verify OTP</CardTitle>
              <CardDescription>
                Enter the 6-digit OTP sent to {email}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp">OTP *</Label>
                  <Input
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    required
                    disabled={verifyingOtp}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep('email')}
                    disabled={verifyingOtp}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button type="submit" disabled={verifyingOtp || !otp} className="flex-1">
                    {verifyingOtp ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</>
                    ) : (
                      'Verify OTP'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Story Form */}
        {step === 'form' && (
          <div className="space-y-6">
            {/* Expert Written Banner - Top Priority */}
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 p-1">
              <div className="bg-background rounded-lg p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-3 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold rounded-full">
                        PREMIUM
                      </span>
                      <span className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                        Get Expert Written Story
                      </span>
                    </div>
                    <p className="text-muted-foreground mb-3">
                      Our professional writers will craft a compelling story for your startup. Skip the questionnaire and let experts handle everything!
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Professional Quality</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Fast Turnaround</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>SEO Optimized</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                      ₹1,999
                    </div>
                    <Button
                      onClick={handleExpertWrittenClick}
                      disabled={saving}
                      size="lg"
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-8"
                    >
                      {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Get Started →'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t-2 border-dashed" />
              </div>
              <div className="relative flex justify-center text-sm uppercase">
                <span className="bg-background px-4 text-muted-foreground font-semibold">OR</span>
              </div>
            </div>

            {/* Write Your Own Story Section */}
            {submissionType !== 'Self Written' ? (
              <Card className="border-2 border-blue-500/20 hover:border-blue-500/40 transition-all">
                <CardContent className="py-6">
                  <div className="text-center space-y-4">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                      <FileEdit className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-blue-900">Write Your Own Story</span>
                    </div>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                      Prefer to craft your startup story yourself? Use our rich text editor to write a compelling narrative with complete creative control.
                    </p>
                    <Button
                      type="button"
                      size="lg"
                      onClick={() => setSubmissionType('Self Written')}
                      disabled={saving}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8"
                    >
                      <FileEdit className="h-5 w-5 mr-2" />
                      Start Writing
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              // Show Self Written Editor when in Self Written mode
              <Card>
                <CardContent className="py-6">
                  <SelfWrittenEditor
                    articleContent={articleContent}
                    setArticleContent={setArticleContent}
                    saving={saving}
                    handleSave={handleSave}
                    setSubmissionType={setSubmissionType}
                  />
                </CardContent>
              </Card>
            )}

            {/* Divider before Questionnaire */}
            {submissionType !== 'Self Written' && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t-2 border-dashed" />
                  </div>
                  <div className="relative flex justify-center text-sm uppercase">
                    <span className="bg-background px-4 text-muted-foreground font-semibold">OR</span>
                  </div>
                </div>

                {/* Simple Option Banner */}
                <div className="text-center py-4 px-6 bg-green-50 border-2 border-green-200 rounded-lg">
                  <p className="text-base text-green-900 font-medium">
                    Want to keep it simple? Just answer the below questions and we will publish the story as it is.
                  </p>
                </div>
              </>
            )}

            {/* Main Form Card - Questionnaire */}
            {submissionType !== 'Self Written' && (
              <Card>
                <CardHeader>
                  <CardTitle>Share Your Startup Story</CardTitle>
                  <CardDescription>
                    Answer these questions to help us craft your story
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
              {/* Banner Photo Upload */}
              <div className="space-y-2 p-4 border border-border rounded-lg bg-accent/5">
                <Label htmlFor="banner_photo" className="text-base font-semibold">
                  Banner Photo (Team / Office / Product) *
                </Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Upload a photo that represents your startup - team photo, office space, or product image
                </p>
                <Input
                  id="banner_photo"
                  type="file"
                  accept="image/*"
                  onChange={handleBannerUpload}
                />
                {bannerPhoto && (
                  <div className="mt-3">
                    <img src={bannerPhoto} alt="Banner preview" className="h-40 w-full object-cover rounded-lg" />
                  </div>
                )}
              </div>

              {/* Founder Profile Pictures */}
              {startup?.founders && startup.founders.length > 0 && (
                <div className="space-y-3 p-4 border border-border rounded-lg bg-accent/5">
                  <Label className="text-base font-semibold">
                    Founder Profile Pictures *
                  </Label>
                  <p className="text-sm text-muted-foreground mb-3">
                    Upload profile pictures for each founder
                  </p>
                  {startup.founders.map((founder, index) => (
                    <div key={index} className="space-y-2">
                      <Label htmlFor={`founder_${index}`} className="text-sm font-medium">
                        {founder.name} ({founder.email})
                      </Label>
                      <Input
                        id={`founder_${index}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFounderProfileUpload(founder.email, e)}
                      />
                      {founderProfiles.find(p => p.founder_email === founder.email)?.profile_picture && (
                        <img 
                          src={founderProfiles.find(p => p.founder_email === founder.email).profile_picture} 
                          alt={`${founder.name} preview`}
                          className="h-24 w-24 object-cover rounded-full border-2 border-primary"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Questionnaire Section */}
              <>
                  {/* Question 1 */}
                  <div className="space-y-2">
                    <Label htmlFor="brief_info">
                      1. Give a brief info about your startup? *
                    </Label>
                <Textarea
                  id="brief_info"
                  name="brief_info"
                  value={formData.brief_info}
                  onChange={handleChange}
                  placeholder="Tell us about your startup..."
                  rows={4}
                  required
                />
              </div>

              {/* Question 2 */}
              <div className="space-y-2">
                <Label htmlFor="startup_journey">
                  2. What made you start your journey and what problem does it solve? *
                </Label>
                <Textarea
                  id="startup_journey"
                  name="startup_journey"
                  value={formData.startup_journey}
                  onChange={handleChange}
                  placeholder="Share your journey and the problem you're solving..."
                  rows={4}
                  required
                />
              </div>

              {/* Question 3 */}
              <div className="space-y-2">
                <Label htmlFor="founder_background">
                  3. Tell us about yourself, your previous jobs/ventures? What were you doing before this startup? *
                </Label>
                <Textarea
                  id="founder_background"
                  name="founder_background"
                  value={formData.founder_background}
                  onChange={handleChange}
                  placeholder="Share your background..."
                  rows={4}
                  required
                />
              </div>

              {/* Question 4 */}
              <div className="space-y-2">
                <Label htmlFor="location_reason">
                  4. Where is your startup based out of? Why do you think that is the best place for you? *
                </Label>
                <Textarea
                  id="location_reason"
                  name="location_reason"
                  value={formData.location_reason}
                  onChange={handleChange}
                  placeholder="Tell us about your location choice..."
                  rows={4}
                  required
                />
              </div>

              {/* Question 5 */}
              <div className="space-y-2">
                <Label htmlFor="founder_paranoia">
                  5. As a startup founder, what are you paranoid about? What keeps you awake at night? *
                </Label>
                <Textarea
                  id="founder_paranoia"
                  name="founder_paranoia"
                  value={formData.founder_paranoia}
                  onChange={handleChange}
                  placeholder="Share your concerns..."
                  rows={4}
                  required
                />
              </div>

              {/* Question 6 */}
              <div className="space-y-2">
                <Label htmlFor="competition_edge">
                  6. Who are your competitions and how are you better than them? *
                </Label>
                <Textarea
                  id="competition_edge"
                  name="competition_edge"
                  value={formData.competition_edge}
                  onChange={handleChange}
                  placeholder="Describe your competitive advantage..."
                  rows={4}
                  required
                />
              </div>

              {/* Question 7 */}
              <div className="space-y-2">
                <Label htmlFor="work_life_balance">
                  7. How hard is it to have a work-life balance as a startup founder and how do you manage it? *
                </Label>
                <Textarea
                  id="work_life_balance"
                  name="work_life_balance"
                  value={formData.work_life_balance}
                  onChange={handleChange}
                  placeholder="Share your work-life balance approach..."
                  rows={4}
                  required
                />
              </div>

              {/* Question 8 */}
              <div className="space-y-2">
                <Label htmlFor="funding_details">
                  8. Have you raised funding? If yes, then we would like to know the details. If no then please tell us if you are looking to raise. *
                </Label>
                <Textarea
                  id="funding_details"
                  name="funding_details"
                  value={formData.funding_details}
                  onChange={handleChange}
                  placeholder="Share your funding status..."
                  rows={4}
                  required
                />
              </div>

              {/* Question 9 */}
              <div className="space-y-2">
                <Label htmlFor="company_excitement">
                  9. What gets you excited about this company? *
                </Label>
                <Textarea
                  id="company_excitement"
                  name="company_excitement"
                  value={formData.company_excitement}
                  onChange={handleChange}
                  placeholder="What excites you the most..."
                  rows={4}
                  required
                />
              </div>

              {/* Question 10 */}
              <div className="space-y-2">
                <Label htmlFor="daily_schedule">
                  10. Tell us how a day in your life looks like? Your schedule for a day right from the time you get up till you hit the bed at night. *
                </Label>
                <Textarea
                  id="daily_schedule"
                  name="daily_schedule"
                  value={formData.daily_schedule}
                  onChange={handleChange}
                  placeholder="Describe your typical day..."
                  rows={4}
                  required
                />
              </div>

              {/* Question 11 */}
              <div className="space-y-2">
                <Label htmlFor="team_info">
                  11. Tell us about your team and how did you meet each other? *
                </Label>
                <Textarea
                  id="team_info"
                  name="team_info"
                  value={formData.team_info}
                  onChange={handleChange}
                    placeholder="Share about your team..."
                    rows={4}
                    required
                  />
                  </div>

                  {/* Action Buttons for Questionnaire */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleSave(true, 'Questionnaire')}
                      disabled={saving}
                      className="flex-1"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save as Draft'}
                    </Button>
                    <Button
                      type="button"
                      variant="editorial"
                      onClick={() => handleSave(false, 'Questionnaire')}
                      disabled={saving}
                      className="flex-1"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit Story'}
                    </Button>
                  </div>
                </>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Expert Written Payment Modal */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Expert Written Story Service
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Pricing */}
            <div className="text-center py-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">One-time payment</p>
              <p className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                ₹1,999
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">What's Included:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <Award className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Professional Writing</p>
                    <p className="text-xs text-muted-foreground">Expert writers craft your story</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Zap className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">SEO Optimized</p>
                    <p className="text-xs text-muted-foreground">Better search visibility</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Multiple Revisions</p>
                    <p className="text-xs text-muted-foreground">Until you're satisfied</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <Clock className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">Quick Delivery</p>
                    <p className="text-xs text-muted-foreground">Ready in 1 week</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Timeline */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold text-blue-900">Delivery Timeline</h4>
              </div>
              <p className="text-sm text-blue-800">
                Your professionally written story will be delivered within <strong>7 days</strong> of payment confirmation. Our team will contact you via email to gather additional details if needed.
              </p>
            </div>

            {/* Test Mode Note */}
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-800">
                <strong>Test Mode:</strong> Use any test card details for testing. No real payment will be charged.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setPaymentModalOpen(false)}
                disabled={processingPayment}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePayNow}
                disabled={processingPayment}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold"
                size="lg"
              >
                {processingPayment ? (
                  <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <>Pay Now ₹1,999</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
